import {
  countryIsoFromBillingName,
  getWorldCountry,
  isoToFlag,
  parseStoredPhoneWorld,
  searchWorldCountries,
  WORLD_COUNTRIES,
  type WorldCountry,
} from '../data/worldCountries';

export type PhoneCountry = WorldCountry & { flag: string; placeholder: string };

function withDisplayFields(country: WorldCountry): PhoneCountry {
  return {
    ...country,
    flag: isoToFlag(country.iso2),
    placeholder: country.iso2 === 'IN' ? '98765 43210' : 'Phone number',
  };
}

export const PHONE_COUNTRIES: PhoneCountry[] = WORLD_COUNTRIES.map(withDisplayFields);

export function getPhoneCountry(iso2: string): PhoneCountry {
  return withDisplayFields(getWorldCountry(iso2));
}

export function countryFromBillingName(country?: string): string {
  return countryIsoFromBillingName(country);
}

export function searchPhoneCountries(query: string, limit = 80): PhoneCountry[] {
  return searchWorldCountries(query, limit).map(withDisplayFields);
}

export function stripNationalDigits(value: string, maxLength: number): string {
  return String(value || '').replace(/\D/g, '').slice(0, maxLength);
}

export function formatNationalDisplay(digits: string, country: PhoneCountry): string {
  const d = stripNationalDigits(digits, country.nationalLength);
  if (country.iso2 === 'IN' && d.length > 5) {
    return `${d.slice(0, 5)} ${d.slice(5)}`.trim();
  }
  if (d.length > 6) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`.trim();
  if (d.length > 3) return `${d.slice(0, 3)} ${d.slice(3)}`.trim();
  return d;
}

export function parseStoredPhone(
  stored: string,
  fallbackCountry = 'IN',
): { countryIso2: string; nationalDigits: string } {
  return parseStoredPhoneWorld(stored, fallbackCountry);
}

export function toE164(countryIso2: string, nationalDigits: string): string {
  const country = getPhoneCountry(countryIso2);
  const digits = stripNationalDigits(nationalDigits, country.nationalLength);
  if (!digits) return '';
  return `${country.dialCode}${digits}`;
}

export function isValidPhoneForCountry(countryIso2: string, nationalDigits: string): boolean {
  const country = getPhoneCountry(countryIso2);
  const digits = stripNationalDigits(nationalDigits, country.nationalLength);
  if (!digits || digits === '9999999999') return false;
  if (country.iso2 === 'IN') {
    return digits.length === 10 && /^[6-9]/.test(digits);
  }
  return digits.length >= Math.min(8, country.nationalLength)
    && digits.length <= country.nationalLength;
}

export function toCashfreeCustomerPhone(countryIso2: string, nationalDigits: string): string {
  const country = getPhoneCountry(countryIso2);
  return stripNationalDigits(nationalDigits, country.nationalLength);
}
